import { useEffect, useMemo, useState } from "react";
import {
  FormControl,
  FormHelperText,
  Grid,
  Box,
  IconButton,
  CircularProgress,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import ClearIcon from "@mui/icons-material/Clear";
import CheckIcon from "@mui/icons-material/Check";
import { withTypes } from "react-final-form";
import { FORM_ERROR } from "final-form";
import { useIntl } from "react-intl";

import {
  Series,
  Module,
  Requirement,
  ISaveRequirement,
  SaveRequirement,
} from "services/KnowfullyAdminClient";

import Field from "components/Field";
import SelectProduct from "components/SelectProduct";
import SelectSkuFormat from "components/SelectSkuFormat";
import useTrashablePromises from "hooks/useTrashablePromises";
import useGlobalContext from "hooks/globalData";
import translateError from "utils/translateError";
import contextStore from "stores/contextStore";
import { Mode } from "types/common-types";

type RequirementFormProps = {
  series: Series;
  module: Module;
  requirement?: Requirement;
  mode: Mode;
  onRequirementUpdated: () => void;
  onRequirementDeleted: () => void;
};

const { Form } = withTypes<ISaveRequirement>();

const RequirementForm = ({
  requirement,
  series,
  module,
  mode,
  onRequirementUpdated,
  onRequirementDeleted,
}: RequirementFormProps) => {
  const init = useMemo(() => requirement, [requirement]);
  const { registerPromise } = useTrashablePromises();
  const { formatMessage: t } = useIntl();
  const { seriesService } = useGlobalContext();
  const [waitingForSubmit, setWaitingForSubmit] = useState(false);
  const isUpdate = mode === Mode.Update;
  const { organizationId } = contextStore.useState();

  // To stop a UI flash during the period where we submit a change and we're awaiting the
  // update of the requirement query, we use a `waitingForSubmit` flag that gets reset
  // when the new requirement is received.
  useEffect(() => setWaitingForSubmit(false), [requirement]);

  const onSubmit = (values: ISaveRequirement) => {
    setWaitingForSubmit(true);
    return registerPromise(
      seriesService.saveRequirement(module.id, new SaveRequirement(values))
    )
      .then(() => {
        onRequirementUpdated();
      })
      .catch((err: any) => {
        console.log(err);
        setWaitingForSubmit(false);
        return { [FORM_ERROR]: translateError(err, t) };
      });
  };

  const onDelete = () => {
    setWaitingForSubmit(true);

    // Only delete if this requirement exists (it's not a new one being created)
    if (requirement) {
      return registerPromise(seriesService.deleteRequirement(requirement!.id))
        .then(() => {
          onRequirementDeleted();
        })
        .catch((err: any) => {
          console.log(err);
          setWaitingForSubmit(false);
        });
    }

    onRequirementDeleted();
  };

  return (
    <Form
      onSubmit={onSubmit}
      initialValues={init}
      render={({ handleSubmit, submitting, dirty, form }) => {
        return (
          <form onSubmit={handleSubmit} style={{ width: "100%" }}>
            <Grid container spacing={1}>
              <Field name="productId" required>
                {({ input, meta }) => (
                  <Grid item xs={5}>
                    <Box display="flex" alignItems="center" height={1}>
                      <FormControl
                        fullWidth
                        error={meta.error && meta.submitFailed}
                      >
                        <SelectProduct
                          disabled={!isUpdate}
                          textFieldProps={{
                            variant: "outlined",
                          }}
                          organizationId={organizationId}
                          size="small"
                          {...input}
                        />
                        {meta.error && meta.submitFailed && (
                          <FormHelperText>{meta.error}</FormHelperText>
                        )}
                      </FormControl>
                    </Box>
                  </Grid>
                )}
              </Field>

              <Field name="skuFormatId">
                {({ input, meta }) => (
                  <Grid item xs={5}>
                    <Box display="flex" alignItems="center" height={1}>
                      <FormControl
                        fullWidth
                        error={meta.error && meta.submitFailed}
                      >
                        <SelectSkuFormat
                          disabled={!isUpdate}
                          textFieldProps={{
                            variant: "outlined",
                          }}
                          size="small"
                          {...input}
                        />
                        {meta.error && meta.submitFailed && (
                          <FormHelperText>{meta.error}</FormHelperText>
                        )}
                      </FormControl>
                    </Box>
                  </Grid>
                )}
              </Field>

              <Grid item xs={2}>
                <Box
                  display="flex"
                  justifyContent="flex-end"
                  alignItems="center"
                  height={1}
                >
                  {submitting && <CircularProgress size={16} />}
                  {!waitingForSubmit && dirty && (
                    <IconButton disabled={!isUpdate} type="submit">
                      <CheckIcon />
                    </IconButton>
                  )}
                  {!waitingForSubmit && dirty && (
                    <IconButton
                      disabled={!isUpdate}
                      onClick={() => form.reset()}
                    >
                      <ClearIcon />
                    </IconButton>
                  )}
                  {!waitingForSubmit && !dirty && (
                    <IconButton disabled={!isUpdate} onClick={onDelete}>
                      <DeleteIcon />
                    </IconButton>
                  )}
                </Box>
              </Grid>
            </Grid>
          </form>
        );
      }}
    />
  );
};

export default RequirementForm;
